import React from 'react';
import { 
  Box, Typography, Paper, Grid, Chip, Divider, Alert, Link, Stack, 
  Table, TableBody, TableCell, TableHead, TableRow,
  AlertTitle,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent
} from "@mui/material";
import SecurityIcon from '@mui/icons-material/Security';
import TimerIcon from '@mui/icons-material/Timer';
import PolicyIcon from '@mui/icons-material/Policy';
import InfoIcon from '@mui/icons-material/Info';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LaunchIcon from '@mui/icons-material/Launch';
import LockIcon from '@mui/icons-material/Lock';
import WarningIcon from '@mui/icons-material/Warning';
import { Application } from "@microsoft/microsoft-graph-types";

interface ConditionalAccessInfoProps {
  application: Application;
}

// Azure Best Practice: Updated session management component with latest Microsoft guidance
const ConditionalAccessInfo: React.FC<ConditionalAccessInfoProps> = ({ application }) => {
  return (
    <Card variant="outlined">
      <CardContent
        sx={{
          background: 'linear-gradient(135deg, #e8eaf6 0%, #f3e5f5 100%)',
          borderBottom: '1px solid #c5cae9',
          mb: 0,
          pb: 2,
          pt: 2,
          px: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0 }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '8px',
              backgroundColor: '#3f51b5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 1.5
            }}
          >
            <SecurityIcon sx={{ color: 'white', fontSize: '0.85rem' }} />
          </Box>
          <Typography
            variant="h6"
            sx={{
              fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
              fontSize: "1.3rem",
              fontWeight: 600,
              color: "#283593",
              letterSpacing: "0.01em",
              flexGrow: 1
            }}
          >
            Conditional Access & Session Management
          </Typography>
          <Chip
            size="small"
            label="Security"
            sx={{
              backgroundColor: '#c5cae9',
              color: '#283593',
              fontWeight: 500
            }}
          />
        </Box>
      </CardContent>
      
      {/* Updated deprecation notice */}
      <CardContent>
      <Alert severity="warning" sx={{ mb: 3 }}>
        <AlertTitle>Important: Token Lifetime Policy Deprecation</AlertTitle>
        <Typography variant="body2">
          Token lifetime policies were officially deprecated on <strong>January 30, 2021</strong> and will be retired. 
          Microsoft strongly recommends migrating to Conditional Access session controls for all new configurations.
          Existing token lifetime policies will continue to work until retirement, but no new policies can be created.
        </Typography>
      </Alert>

      {/* Token Lifetime Management Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" fontWeight={600}>Token Lifetime Management</Typography>
        <Typography variant="body2" paragraph sx={{ mt: 1 }}>
          Token lifetimes for this application should be managed through Conditional Access policies. Token lifetime policies are being deprecated in favor of Conditional Access session controls which provide more granular control.
        </Typography>
        
        <Typography variant="body2" sx={{ mt: 2 }}>
          <strong>Recommended approach:</strong>
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="Use Conditional Access sign-in frequency controls" 
              secondary="Configure how often users must reauthenticate" 
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="Configure session persistence settings" 
              secondary="Determine if users remain signed in after closing and reopening browser windows" 
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="Use Continuous Access Evaluation (CAE)" 
              secondary="Enables real-time token revocation based on risk signals" 
            />
          </ListItem>
        </List>
        
        <Button 
          variant="outlined" 
          color="primary"
          startIcon={<LaunchIcon />}
          href="https://learn.microsoft.com/en-us/entra/identity/conditional-access/howto-conditional-access-session-lifetime"
          target="_blank"
          sx={{ mt: 2 }}
        >
          Learn more about session management
        </Button>
      </Box>
      
      <Grid container spacing={3}>
        {/* Updated Default Token Lifetimes */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <TimerIcon sx={{ mr: 1, fontSize: '1.2rem', color: 'primary.main' }} />
              Default Token Lifetimes
            </Typography>
            <Divider sx={{ my: 1 }} />
            
            <Table size="small" sx={{ mt: 2 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Token Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Default Lifetime</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Configurable</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Access Token</TableCell>
                  <TableCell>1 hour</TableCell>
                  <TableCell>No (fixed)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>ID Token</TableCell>
                  <TableCell>1 hour</TableCell>
                  <TableCell>No (fixed)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>SAML Token</TableCell>
                  <TableCell>1 hour</TableCell>
                  <TableCell>No (fixed)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Refresh Token (Single-tenant)</TableCell>
                  <TableCell>90 days (inactive) / 400 days (active)</TableCell>
                  <TableCell>Indirectly via sign-in frequency</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Refresh Token (Multi-tenant)</TableCell>
                  <TableCell>90 days (inactive) / Until revoked (active)</TableCell>
                  <TableCell>Indirectly via sign-in frequency</TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Important:</strong> While refresh token lifetimes cannot be directly configured, 
                Conditional Access sign-in frequency controls can require users to reauthenticate 
                before their refresh tokens would naturally expire, effectively limiting session duration.
              </Typography>
            </Alert>
            
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Note: While these are the default values, Microsoft recommends managing session and authentication lifetimes through Conditional Access policies rather than using token lifetime policies which are deprecated.
              </Typography>
            </Box>
          </Paper>
        </Grid>
      
        {/* Updated Conditional Access Session Controls */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <PolicyIcon sx={{ mr: 1, fontSize: '1.2rem', color: 'primary.main' }} />
              Recommended Session Controls
            </Typography>
            <Divider sx={{ my: 1 }} />
            
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" paragraph>
                Microsoft recommends using these Conditional Access controls to manage session behavior:
              </Typography>
              
              <Stack spacing={1.5}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Chip size="small" label="Sign-in frequency" color="primary" variant="outlined" sx={{ mt: 0.5, mr: 1.5, minWidth: 140 }} />
                  <Typography variant="body2">
                    Determines how often a user must re-authenticate (e.g., every 4 hours, every 7 days)
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Chip size="small" label="Persistent browser" color="primary" variant="outlined" sx={{ mt: 0.5, mr: 1.5, minWidth: 140 }} />
                  <Typography variant="body2">
                    Controls whether users can remain signed in after closing and reopening their browser
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Chip size="small" label="Device compliance" color="primary" variant="outlined" sx={{ mt: 0.5, mr: 1.5, minWidth: 140 }} />
                  <Typography variant="body2">
                    Requires devices to be marked as compliant before granting access
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Chip size="small" label="App protection" color="primary" variant="outlined" sx={{ mt: 0.5, mr: 1.5, minWidth: 140 }} />
                  <Typography variant="body2">
                    Enforces app protection policies when accessing cloud apps
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Chip size="small" label="Use Global Secure Access" color="secondary" variant="outlined" sx={{ mt: 0.5, mr: 1.5, minWidth: 140 }} />
                  <Typography variant="body2">
                    Route traffic through Microsoft's Security Service Edge solution
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Chip size="small" label="Disable resilience defaults" color="secondary" variant="outlined" sx={{ mt: 0.5, mr: 1.5, minWidth: 140 }} />
                  <Typography variant="body2">
                    Override default resilience settings for stricter security
                  </Typography>
                </Box>
              </Stack>
            </Box>
            
            <Link 
              href="https://learn.microsoft.com/en-us/entra/identity/conditional-access/howto-conditional-access-session-lifetime" 
              target="_blank"
              variant="body2"
              sx={{ display: 'block', mt: 2 }}
            >
              Microsoft documentation on session management
            </Link>
          </Paper>
        </Grid>
        
        {/* Updated Continuous Access Evaluation */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <VerifiedUserIcon sx={{ mr: 1, fontSize: '1.2rem', color: 'primary.main' }} />
              Continuous Access Evaluation (CAE)
            </Typography>
            <Divider sx={{ my: 1 }} />
            
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" paragraph>
                Continuous Access Evaluation (CAE) extends token lifetimes while allowing Microsoft Entra ID 
                to revoke access in real-time when critical events occur or policies are updated.
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ borderLeft: '4px solid rgba(25, 118, 210, 0.12)', pl: 2, py: 0.5 }}>
                    <Typography variant="subtitle2" gutterBottom>Benefits</Typography>
                    <Box component="ul" sx={{ pl: 2, mt: 0, mb: 0 }}>
                      <Box component="li">
                        <Typography variant="body2">Near real-time security policy enforcement</Typography>
                      </Box>
                      <Box component="li">
                        <Typography variant="body2">Improved user experience with fewer authentication prompts</Typography>
                      </Box>
                      <Box component="li">
                        <Typography variant="body2">Enhanced security without sacrificing usability</Typography>
                      </Box>
                      <Box component="li">
                        <Typography variant="body2">Extended access token lifetimes (up to 28 hours)</Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box sx={{ borderLeft: '4px solid rgba(25, 118, 210, 0.12)', pl: 2, py: 0.5 }}>
                    <Typography variant="subtitle2" gutterBottom>Critical Events</Typography>
                    <Box component="ul" sx={{ pl: 2, mt: 0, mb: 0 }}>
                      <Box component="li">
                        <Typography variant="body2">User account disabled or deleted</Typography>
                      </Box>
                      <Box component="li">
                        <Typography variant="body2">Password change or reset</Typography>
                      </Box>
                      <Box component="li">
                        <Typography variant="body2">MFA enabled for user</Typography>
                      </Box>
                      <Box component="li">
                        <Typography variant="body2">Administrator revokes refresh tokens</Typography>
                      </Box>
                      <Box component="li">
                        <Typography variant="body2">Detected user risk level change</Typography>
                      </Box>
                      <Box component="li">
                        <Typography variant="body2">Location-based risk policy changes</Typography>
                      </Box>
                      <Box component="li">
                        <Typography variant="body2">Session risk policy updates</Typography>
                      </Box>
                      <Box component="li">
                        <Typography variant="body2">IP address changes (for some policies)</Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 3, backgroundColor: 'rgba(25, 118, 210, 0.04)', p: 2, borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <InfoIcon sx={{ mr: 1, color: 'info.main', mt: 0.25 }} />
                  <Typography variant="body2">
                    <strong>Note:</strong> Applications must support CAE to benefit from this capability. CAE-capable applications include Microsoft 365 apps and other apps that implement the CAE protocol.
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            <Link 
              href="https://learn.microsoft.com/en-us/entra/identity/conditional-access/concept-continuous-access-evaluation" 
              target="_blank"
              variant="body2"
              sx={{ display: 'block', mt: 2 }}
            >
              Learn more about Continuous Access Evaluation
            </Link>
          </Paper>
        </Grid>

        {/* Migration Guidance Section */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'rgba(255, 152, 0, 0.04)' }}>
            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <WarningIcon sx={{ mr: 1, fontSize: '1.2rem', color: 'warning.main' }} />
              Migration from Token Lifetime Policies
            </Typography>
            <Divider sx={{ my: 1 }} />
            
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" paragraph>
                If your organization is currently using token lifetime policies, follow these steps to migrate to Conditional Access:
              </Typography>
              
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Audit existing token lifetime policies" 
                    secondary="Document current settings and identify which applications are affected" 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Create equivalent Conditional Access policies" 
                    secondary="Use sign-in frequency and session controls to replicate behavior" 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Test in report-only mode" 
                    secondary="Validate that new policies work as expected before enforcement" 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Enable Conditional Access policies" 
                    secondary="Switch from report-only to enforcement mode" 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Remove token lifetime policies" 
                    secondary="Clean up deprecated policies after successful migration" 
                  />
                </ListItem>
              </List>
              
              <Button 
                variant="outlined" 
                color="warning"
                startIcon={<LaunchIcon />}
                href="https://learn.microsoft.com/en-us/entra/identity/conditional-access/howto-conditional-access-session-lifetime#migrate-from-token-lifetime-policies"
                target="_blank"
                sx={{ mt: 2 }}
              >
                Migration guidance documentation
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </CardContent>
    </Card>
  );
};

export default ConditionalAccessInfo;